import { useState, useEffect } from 'react';
import { api } from '../api';
import { useTripBuilder } from '../TripBuilderContext';

export default function TransferHub() {
  const { amexTotal: amex, chaseTotal: chase } = useTripBuilder();
  const [amexPartners, setAmexPartners] = useState([]);
  const [chasePartners, setChasePartners] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getTransferPartners('amex_mr'),
      api.getTransferPartners('chase_ur'),
      api.getTransferBonuses(),
    ]).then(([a, c, b]) => {
      setAmexPartners(a);
      setChasePartners(c);
      setBonuses(b);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-brand-ink/65">Loading transfer partners…</div>;

  // Find overlapping programs
  const amexPrograms = new Set(amexPartners.map(p => p.airline_program));
  const chasePrograms = new Set(chasePartners.map(p => p.airline_program));
  const overlap = [...amexPrograms].filter(p => chasePrograms.has(p));

  const activeBonuses = bonuses.filter(b => b.is_active);

  return (
    <div className="space-y-8 pb-16">
      <div>
        <h1 className="text-2xl font-bold">Transfer Hub</h1>
        <p className="text-brand-ink/70 text-sm mt-1">Where your points can go. Map every transfer path across programs.</p>
      </div>

      {/* Transfer Rules */}
      <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 space-y-2">
        <div className="font-semibold text-gold text-sm">TRANSFER RULES — READ THESE FIRST</div>
        <div className="text-xs text-brand-ink/75 space-y-1">
          <div><strong className="text-brand-ink/85">1.</strong> Transfers are ONE-WAY and IRREVERSIBLE.</div>
          <div><strong className="text-brand-ink/85">2.</strong> NEVER transfer before confirming availability on the airline/hotel website.</div>
          <div><strong className="text-brand-ink/85">3.</strong> Most transfers are instant. ANA takes 2-3 business days.</div>
          <div><strong className="text-brand-ink/85">4.</strong> Transfer bonuses change the math. Check for active bonuses before transferring.</div>
          <div><strong className="text-brand-ink/85">5.</strong> Not all programs are reachable from both banks. Know which bank unlocks which.</div>
          <div><strong className="text-brand-ink/85">6.</strong> ANA requires round-trip bookings and has family member restrictions.</div>
          <div><strong className="text-brand-ink/85">7.</strong> Fuel surcharges vary by booking program — same flight, different cash cost.</div>
          <div><strong className="text-brand-ink/85">8.</strong> Both programs transfer to Aeroplan 1:1, so you can pool Amex + Chase into one Aeroplan account.</div>
        </div>
      </div>

      {/* Active Bonuses */}
      {activeBonuses.length > 0 && (
        <section className="bg-teal/10 border border-teal/30 rounded-xl p-4">
          <div className="text-sm font-semibold text-teal mb-2">ACTIVE TRANSFER BONUSES</div>
          <div className="space-y-2">
            {activeBonuses.map(b => (
              <div key={b.id} className="flex items-center gap-3 text-sm">
                <span className="px-2 py-0.5 bg-teal/20 text-teal rounded text-xs font-bold">+{b.bonus_percentage}%</span>
                <span>{b.source_program === 'amex_mr' ? 'Amex' : 'Chase'} &rarr; {b.airline_program_short}</span>
                {b.end_date && <span className="text-brand-ink/65 text-xs">ends {new Date(b.end_date).toLocaleDateString()}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Amex MR */}
        <div className="space-y-4">
          <div className="bg-blue-900/30 rounded-xl p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-blue-300 uppercase tracking-wider">AMEX MEMBERSHIP REWARDS</div>
                <div className="text-2xl font-bold mt-1">{amex.toLocaleString()}</div>
              </div>
              <div className="text-xs text-brand-ink/65 text-right">
                {amexPartners.filter(p => p.partner_type === 'airline').length} airlines<br/>
                {amexPartners.filter(p => p.partner_type === 'hotel').length} hotels
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {amexPartners.map(p => (
              <PartnerCard key={p.id} partner={p} overlap={chasePrograms.has(p.airline_program)} sourceLabel="Amex" bonuses={activeBonuses} />
            ))}
          </div>
        </div>

        {/* Chase UR */}
        <div className="space-y-4">
          <div className="bg-indigo-900/30 rounded-xl p-4 border border-indigo-500/20">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-indigo-300 uppercase tracking-wider">CHASE ULTIMATE REWARDS</div>
                <div className="text-2xl font-bold mt-1">{chase.toLocaleString()}</div>
              </div>
              <div className="text-xs text-brand-ink/65 text-right">
                {chasePartners.filter(p => p.partner_type === 'airline').length} airlines<br/>
                {chasePartners.filter(p => p.partner_type === 'hotel').length} hotels
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {chasePartners.map(p => (
              <PartnerCard key={p.id} partner={p} overlap={amexPrograms.has(p.airline_program)} sourceLabel="Chase" bonuses={activeBonuses} />
            ))}
          </div>
        </div>
      </div>

      {/* Overlap Section */}
      <section className="bg-navy-light rounded-xl border border-brand-ink/10 p-5">
        <h2 className="text-lg font-bold mb-3">Shared Transfer Partners ({overlap.length})</h2>
        <p className="text-sm text-brand-ink/70 mb-4">
          These programs are reachable from BOTH Amex MR and Chase UR. You can pool points from both banks into any of these.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {overlap.map(p => (
            <span key={p} className="px-3 py-1 bg-brand-ink/10 rounded-lg text-sm">{p}</span>
          ))}
        </div>
        <div className="bg-brand-ink/5 rounded-lg p-3 text-sm text-brand-ink/75 space-y-2">
          <div><strong className="text-brand-ink/85">Pooling Play:</strong> Both programs transfer to Aeroplan 1:1. You could combine {amex.toLocaleString()} Amex + {chase.toLocaleString()} Chase = {(amex + chase).toLocaleString()} Aeroplan for a massive booking.</div>
          <div><strong className="text-brand-ink/85">When to pool:</strong> Only when you need more than {amex.toLocaleString()} for flights. Otherwise, keep Chase for hotels (Hyatt).</div>
          <div><strong className="text-brand-ink/85">When to split:</strong> Usually. Amex for flights + Chase for Hyatt hotels = both pools working independently.</div>
        </div>
      </section>

      {/* Exclusives */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-blue-900/20 rounded-xl border border-blue-500/20 p-4">
          <div className="text-sm font-semibold text-blue-300 mb-2">AMEX EXCLUSIVES</div>
          <div className="space-y-1">
            {amexPartners.filter(p => !chasePrograms.has(p.airline_program)).map(p => (
              <div key={p.id} className="text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                {p.airline_program_short}
                <span className="text-xs text-brand-ink/55">{p.transfer_ratio}:1 &middot; {p.transfer_time_description}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-indigo-900/20 rounded-xl border border-indigo-500/20 p-4">
          <div className="text-sm font-semibold text-indigo-300 mb-2">CHASE EXCLUSIVES</div>
          <div className="space-y-1">
            {chasePartners.filter(p => !amexPrograms.has(p.airline_program)).map(p => (
              <div key={p.id} className="text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                {p.airline_program_short}
                <span className="text-xs text-brand-ink/55">{p.transfer_ratio}:1 &middot; {p.transfer_time_description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PartnerCard({ partner, overlap, sourceLabel, bonuses }) {
  const p = partner;
  const activeBonus = bonuses.find(b => b.transfer_partner_id === p.id);
  const isExclusive = !overlap;

  return (
    <div className={`bg-navy-lighter rounded-lg border border-brand-ink/5 p-3 flex items-center gap-3 ${activeBonus ? 'ring-1 ring-teal/30' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
        p.partner_type === 'hotel' ? 'bg-purple-900/50 text-purple-300' : 'bg-brand-ink/10 text-brand-ink/75'
      }`}>
        {p.partner_type === 'hotel' ? 'H' : 'A'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{p.airline_program_short}</span>
          {isExclusive && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              sourceLabel === 'Amex' ? 'bg-blue-900/50 text-blue-300' : 'bg-indigo-900/50 text-indigo-300'
            }`}>
              {sourceLabel.toUpperCase()} ONLY
            </span>
          )}
          {activeBonus && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal/20 text-teal font-bold">+{activeBonus.bonus_percentage}%</span>
          )}
        </div>
        <div className="text-xs text-brand-ink/65">{p.transfer_ratio}:1 &middot; {p.transfer_time_description}</div>
      </div>
      <div className={`text-xs px-2 py-1 rounded ${
        p.is_instant ? 'bg-teal/10 text-teal' : 'bg-gold/10 text-gold'
      }`}>
        {p.is_instant ? 'Instant' : 'Delayed'}
      </div>
    </div>
  );
}
